import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { classNames } from "@/utils/classNames";
import { CollapseGrabber } from "@/components/CollapseGrabber/CollapseGrabber";

import { ListPerson } from "../../List/schema/list";
import { DetailView } from "../../List/pages/Unassigned/components/DetailView";

interface LeadDetailRailProps {
	/** Whether the detail rail is expanded. */
	show: boolean;
	/** Toggle the rail open/closed (also passed to the panel so it can close itself). */
	setShow: (value: boolean) => void;
	/** The lead shown in the panel; nothing renders until one is selected. */
	listPerson?: ListPerson;
	/**
	 * Extra classes for the collapse/expand grabber (e.g. a higher z-index). Inbox
	 * stacks its rail above the chatlog (`z-[50000]`); Outbox uses the default.
	 */
	grabberClassName?: string;
	/** List id forwarded to the detail view (Review to send needs it for actions). */
	listId?: number;
	/**
	 * In-flow column variant (PREC-3850): the rail reserves its width inside the
	 * flex row instead of overlaying the thread, and collapses to a slim strip.
	 * The overlay variant stays for the Review/Compose surfaces.
	 */
	docked?: boolean;
	/**
	 * Where the panel's "Reach out to {name}" should go on this surface. The Outbox hands over its
	 * own Chat tab; a surface whose composer already *is* the middle column (Inbox, Review, Compose)
	 * passes nothing and the button is left out rather than shown dead (PREC-4107).
	 */
	onReachOut?: () => void;
}

/**
 * Collapsible right-hand lead detail rail shared by the Messages surfaces
 * (Inbox, Outbox, Compose, Review): a {@link DetailView} that slides in/out via
 * the unified {@link CollapseGrabber} on its left edge (PREC-3596).
 */
export const LeadDetailRail: FunctionComponent<LeadDetailRailProps> = ({
	show,
	setShow,
	listPerson,
	grabberClassName,
	listId,
	docked = false,
	onReachOut,
}) => {
	const { t } = useTranslation();

	return (
		<div
			className={classNames(
				"bg-white scrollbar-hide transition-all z-[500] duration-300 h-full border-l border-border-light",
				docked
					? classNames(
							// Full content height beside the tab/filter bars (PREC-3850 round 2):
							// no leftover overlay padding/margin — DetailView's h-full fills the rail.
							"relative shrink-0 min-h-0",
							show ? "w-[380px] min-w-[380px]" : "w-[35px] min-w-[35px]"
						)
					: classNames(
							"w-[450px] absolute top-0 right-0 border-t pb-[80px] mb-4",
							show ? "translate-x-[initial] pr-0" : "translate-x-[400px] pr-4"
						)
			)}
		>
			<CollapseGrabber
				ariaLabel={show ? t("basics.collapse") : t("basics.expand")}
				className={grabberClassName}
				collapsed={!show}
				edge="left"
				onToggle={() => setShow(!show)}
			/>
			{show && listPerson && (
				<DetailView
					inline
					// All Messages surfaces have their own chat affordances, so the rail never offers the
					// per-lead Chat tab. It does keep the campaign actions: pausing a queued send is the
					// whole point of standing in the Outbox (PREC-4107).
					hideChatTab
					listId={listId}
					listPerson={listPerson}
					setShowDetailView={setShow}
					onHostReachOut={onReachOut}
				/>
			)}
		</div>
	);
};
